
import { useState, useEffect } from 'react';
import { useAdminCheck } from './useAdminCheck';

/**
 * Hook para persistir a clínica selecionada pelo administrador
 * 
 * O que faz:
 * - Salva e recupera a clínica selecionada do localStorage
 * - Sincroniza a seleção entre todas as páginas administrativas
 * - Limpa a persistência quando necessário (logout, clínica inválida)
 * 
 * Onde é usado:
 * - Páginas administrativas (Dashboard, Chat, Pipeline)
 * - Qualquer página que use AdminClinicSelector
 * 
 * Como se conecta:
 * - Usa localStorage para persistência entre sessões
 * - Integra com useAdminCheck para validar permissões
 * - Retorna estado e funções para gerenciar a seleção
 */

interface ClinicaPersistida {
  id: string;
  nome: string;
  email: string;
  [key: string]: any; // Permitir outras propriedades da clínica
}

const STORAGE_KEY = 'admin_clinica_selecionada';

export const useAdminClinicaPersistente = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const [clinicaSelecionada, setClinicaSelecionada] = useState<ClinicaPersistida | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar clínica persistida do localStorage na inicialização
  useEffect(() => {
    if (adminLoading) return;

    if (isAdmin) {
      try {
        const clinicaPersistida = localStorage.getItem(STORAGE_KEY);
        if (clinicaPersistida) {
          const clinicaData = JSON.parse(clinicaPersistida);
          
          // Validar se os dados estão em formato correto
          if (clinicaData && clinicaData.id && clinicaData.nome) {
            console.log('[useAdminClinicaPersistente] Carregando clínica persistida:', clinicaData.nome);
            setClinicaSelecionada(clinicaData);
          } else {
            console.warn('[useAdminClinicaPersistente] Dados de clínica inválidos no localStorage');
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('[useAdminClinicaPersistente] Erro ao carregar clínica do localStorage:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    
    setIsLoading(false);
  }, [isAdmin, adminLoading]);

  // Função para selecionar e persistir uma clínica
  const selecionarClinica = (clinica: ClinicaPersistida | null) => {
    if (!isAdmin) {
      console.warn('[useAdminClinicaPersistente] Tentativa de selecionar clínica sem permissões de admin');
      return;
    }

    setClinicaSelecionada(clinica);

    if (clinica) {
      try {
        // Salvar no localStorage apenas os dados essenciais
        const dadosParaPersistir = {
          id: clinica.id,
          nome: clinica.nome,
          email: clinica.email,
          // Incluir outros campos relevantes que possam ser necessários
          status: clinica.status,
          plano_contratado: clinica.plano_contratado,
          total_leads: clinica.total_leads,
          total_usuarios: clinica.total_usuarios,
          taxa_conversao: clinica.taxa_conversao
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(dadosParaPersistir));
        console.log('[useAdminClinicaPersistente] Clínica selecionada e persistida:', clinica.nome);
      } catch (error) {
        console.error('[useAdminClinicaPersistente] Erro ao salvar clínica no localStorage:', error);
      }
    } else {
      // Limpar seleção
      localStorage.removeItem(STORAGE_KEY);
      console.log('[useAdminClinicaPersistente] Seleção de clínica limpa');
    }
  };

  // Função para limpar a persistência (útil para logout)
  const limparPersistencia = () => {
    setClinicaSelecionada(null);
    localStorage.removeItem(STORAGE_KEY);
    console.log('[useAdminClinicaPersistente] Persistência limpa manualmente');
  };

  // Função para validar se a clínica persistida ainda é válida
  const validarClinicaPersistida = (clinicasDisponiveis: any[]) => {
    if (!clinicaSelecionada) return;

    const clinicaValida = clinicasDisponiveis.find(c => c.id === clinicaSelecionada.id);
    if (!clinicaValida) {
      console.warn('[useAdminClinicaPersistente] Clínica persistida não encontrada, limpando seleção');
      selecionarClinica(null);
    }
  };

  return {
    clinicaSelecionada,
    selecionarClinica,
    limparPersistencia,
    validarClinicaPersistida,
    isLoading: isLoading || adminLoading,
    isAdmin
  };
};
